/*
 * app.pathways.js - Memora biological pathway diagrams
 *
 * Renders a ```memora-pathway fenced block into a themed inline SVG figure.
 *
 * WHY A DSL AND NOT MODEL-DRAWN SVG
 * ---------------------------------
 * Same decision as the answer icon registry (app.icons.js): the model emits a
 * tiny, checkable description and Memora owns every pixel. A Flash-class model
 * asked for raw SVG or for coordinates produces overlapping boxes, ragged
 * spacing, and occasionally invalid path data. Asked for "A -> B : enzyme" it is
 * reliable, because that is just the mechanism chain it already writes in prose.
 * Layout quality is therefore a property of this file, not of the weather.
 *
 * GRAMMAR (the whole thing - see MEMORA_PATHWAY_PROMPT_CONTRACT for the copy
 * that ships in the prompt)
 *
 *   title: Arachidonic Acid Cascade      header line, optional
 *   subtitle: Slides 12-14               small header line, optional
 *   side: right                          left | right | full (default full)
 *   note: ...                            footnote under the diagram, optional
 *
 *   A -> B : enzyme                      conversion; ": enzyme" is optional
 *   LH -> enzyme label : stimulates      positive regulation of a labelled edge
 *   A -| B                               inhibition; B may be a node OR an
 *                                        enzyme label used on some edge
 *   Name {role}                          role tag on either side of an edge
 *   group: Title | subtitle : A; B       presentation group of starting boxes
 *
 *   roles: start | substrate | product | effect | drug | enzyme
 *
 * Everything else on a line is ignored rather than fatal. A block that produces
 * no edges renders nothing at all and the caller falls back to a normal code
 * block, so a malformed diagram can never blank out a section of the guide.
 *
 * Flat script in the shared global scope, loaded from index.html BEFORE
 * js/modules/app.ai.js. All top-level names are prefixed (npm run verify:globals).
 */

/* ==========================================================================
   GEOMETRY CONSTANTS
   Tuned against the arachidonic-acid case (4 ranks, 3-way branch, 3 inhibitors)
   which is the densest shape a lecture realistically produces.
   ========================================================================== */

const MEMORA_PATHWAY_METRICS = Object.freeze({
    fontSize: 13,
    lineHeight: 17,
    nodePadX: 14,
    nodePadY: 10,
    nodeMinWidth: 72,
    nodeMaxWidth: 196,
    nodeMaxLines: 3,
    rankGap: 58,            // minimum gap between rank bands with no labels in it
    labelledRankGap: 76,    // minimum gap when an enzyme label sits beside a drop
    siblingGap: 26,         // minimum horizontal space between boxes in a rank
    busDrop: 16,            // trunk length from a box to its first bus lane
    laneStep: 12,           // vertical offset between staggered bus lanes
    laneClearance: 14,      // horizontal clearance two buses need to share a lane
    portSpread: 22,         // spacing between arrows landing on one box
    cornerRadius: 10,
    edgeLabelFontSize: 11.5,
    edgeLabelGap: 9,        // label distance from the arrow it describes
    inhibitorGap: 34,       // horizontal offset from an inhibitor to its target
    viaWidth: 16,           // slot reserved on each rank a long edge passes through
    arrowLength: 9,
    arrowWidth: 8,
    padding: 18             // canvas margin around the whole graph
});

const MEMORA_PATHWAY_ROLES = Object.freeze({
    start: 'start',
    substrate: 'substrate',
    product: 'product',
    effect: 'effect',
    drug: 'drug',
    enzyme: 'enzyme'
});

// Forgiveness layer, same idea as the icon aliases: a plausible-but-wrong role
// still lands somewhere sensible instead of being dropped.
const MEMORA_PATHWAY_ROLE_ALIASES = Object.freeze({
    source: 'start', input: 'start', precursor: 'start', origin: 'start',
    intermediate: 'substrate', substrate: 'substrate', metabolite: 'substrate',
    output: 'product', mediator: 'product', end: 'product',
    outcome: 'effect', result: 'effect', clinical: 'effect', symptom: 'effect',
    sign: 'effect', response: 'effect',
    inhibitor: 'drug', blocker: 'drug', med: 'drug', medication: 'drug',
    treatment: 'drug', therapy: 'drug', antagonist: 'drug',
    catalyst: 'enzyme', protein: 'enzyme'
});

const MEMORA_PATHWAY_MAX_NODES = 40;
const MEMORA_PATHWAY_MAX_EDGES = 60;

// Stable pathway keys are content-addressed so Study Guide edits can persist
// per-diagram state across a full markdown re-render. The counter is reserved
// for the vanishingly rare case where two different normalized sources produce
// the same 32-bit hash during one runtime.
let memoraPathwayUidCounter = 0;
const memoraPathwayHashSources = new Map();
const memoraPathwayCollisionKeys = new Map();
let memoraPathwayMeasureContext = null;

// One presentation policy for DSL pathways and generated flow visuals. Content identities
// distribute diagrams between designs without a new store or a different choice on resize/export.
const MEMORA_PATHWAY_PRESENTATION = Object.freeze({
    styles: Object.freeze(['copper-circuit', 'editorial']),
    minimumScale: 0.85,
    select(seed) {
        const value = parseInt(hashMemoraPathwayFNV1a(String(seed || 'pathway')), 16);
        return this.styles[value % this.styles.length];
    },
    mark(x, y, ordinal, terminal = false) {
        return terminal
            ? `<circle class="memora-presentation-step" cx="${x}" cy="${y - 4}" r="2.6"/>`
            : `<text class="memora-presentation-step" x="${x + 2}" y="${y + 2}" text-anchor="end">${Number(ordinal) || 1}</text>`;
    },
    svg(markup, seed) {
        const style = this.select(seed);
        // Styles travel with the SVG into thumbnails, expanded views and HTML exports.
        return markup.replace(/<svg\b([^>]*)>/, `<svg$1 data-pathway-style="${style}"><style>${this.css}</style>`);
    },
    css: `
svg[data-pathway-style] { --mp-copper:var(--g1,#cf956c); --mp-teal:#79b7a8; --mp-ink:var(--text-main,#f0e7d9); --mp-surface:var(--bg,#23221f); }
svg[data-pathway-style] :is(g.memora-pathway-node[data-role],g.memora-artifact-node,g.memora-diagram-shapes) .memora-presentation-box { fill:color-mix(in srgb,var(--mp-ink) 7%,var(--mp-surface)); fill-opacity:1; stroke:color-mix(in srgb,var(--mp-copper) 55%,var(--mp-surface)); stroke-opacity:1; stroke-width:1.1; }
svg[data-pathway-style] :is(g.memora-pathway-node[data-role],g.memora-artifact-node,g.memora-diagram-shapes) .memora-presentation-box[data-terminal] { fill:color-mix(in srgb,var(--mp-teal) 20%,var(--mp-surface)); stroke:color-mix(in srgb,var(--mp-teal) 60%,var(--mp-surface)); }
svg[data-pathway-style] :is(g.memora-pathway-node[data-role],g.memora-artifact-node,g.memora-diagram-marks) .memora-presentation-text { fill:var(--mp-ink); font-weight:500; }
svg[data-pathway-style] .memora-presentation-step { display:none; }
svg[data-pathway-style] .memora-presentation-edge { stroke:color-mix(in srgb,var(--mp-copper) 55%,var(--mp-ink)); stroke-width:1.3; }
svg[data-pathway-style="editorial"] :is(g.memora-pathway-node[data-role],g.memora-artifact-node,g.memora-diagram-shapes) .memora-presentation-box,
svg[data-pathway-style="editorial"] :is(g.memora-pathway-node[data-role],g.memora-artifact-node,g.memora-diagram-shapes) .memora-presentation-box[data-terminal] { fill:none; stroke:none; filter:none; }
svg[data-pathway-style="editorial"] :is(g.memora-pathway-node[data-role],g.memora-artifact-node,g.memora-diagram-marks) .memora-presentation-text { font-family:Georgia,'Times New Roman',serif; font-weight:400; }
svg[data-pathway-style="editorial"] .memora-presentation-step { display:inline; font:18px Georgia,serif; fill:var(--mp-copper); paint-order:stroke; stroke:var(--mp-surface); stroke-width:6; stroke-linejoin:round; }
svg[data-pathway-style="editorial"] circle.memora-presentation-step { fill:var(--mp-teal); }
svg[data-pathway-style="editorial"] :is(g.memora-pathway-node[data-role],g.memora-artifact-node,g.memora-diagram-marks) .memora-presentation-text[data-terminal] { fill:color-mix(in srgb,var(--mp-teal) 60%,var(--mp-ink)); }
svg[data-pathway-style="editorial"] .memora-presentation-edge,
svg[data-pathway-style="editorial"] .memora-pathway-edge { stroke-width:1; }
svg[data-pathway-style] g.memora-pathway-node[data-role="drug"] .memora-presentation-text { fill:var(--memora-pathway-block,#c87d72); }
`
});



function normalizeMemoraPathwaySourceForKey(source = '') {
    return String(source || '')
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map((line) => line.replace(/[ \t]+$/g, ''))
        .join('\n')
        .trim();
}

function hashMemoraPathwayFNV1a(value = '') {
    let hash = 0x811c9dc5;
    const text = String(value || '');
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}

function getMemoraPathwayKey(source = '') {
    const normalizedSource = normalizeMemoraPathwaySourceForKey(source);
    if (!normalizedSource) return '';
    const hash = hashMemoraPathwayFNV1a(normalizedSource);
    const baseKey = `memora-pathway-${hash}`;
    const knownSource = memoraPathwayHashSources.get(hash);
    if (!knownSource) {
        memoraPathwayHashSources.set(hash, normalizedSource);
        return baseKey;
    }
    if (knownSource === normalizedSource) return baseKey;
    const collisionSeed = `${hash}\u0000${normalizedSource}`;
    const knownCollisionKey = memoraPathwayCollisionKeys.get(collisionSeed);
    if (knownCollisionKey) return knownCollisionKey;
    memoraPathwayUidCounter += 1;
    const collisionKey = `${baseKey}-${memoraPathwayUidCounter}`;
    memoraPathwayCollisionKeys.set(collisionSeed, collisionKey);
    return collisionKey;
}

function encodeMemoraPathwaySource(source = '') {
    try {
        return encodeURIComponent(String(source || ''));
    } catch (_error) {
        return encodeURIComponent(String(source || '').replace(/[\uD800-\uDFFF]/g, '\uFFFD'));
    }
}

/* ==========================================================================
   TEXT MEASUREMENT
   Canvas when we have a DOM, a per-character width table when we do not (node
   test harness). The table matters: a wrong estimate is what produces boxes
   whose text spills past the border.
   ========================================================================== */

const MEMORA_PATHWAY_NARROW_CHARS = new Set([...'ijltfIr.,:;\'"`|!()[]{}/\\-']);
const MEMORA_PATHWAY_WIDE_CHARS = new Set([...'mwMWQ@%&']);

function measureMemoraPathwayText(text, fontSize = MEMORA_PATHWAY_METRICS.fontSize, weight = 700) {
    const value = String(text || '');
    if (!value) return 0;
    if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
        try {
            if (!memoraPathwayMeasureContext) {
                memoraPathwayMeasureContext = document.createElement('canvas').getContext('2d');
            }
            if (memoraPathwayMeasureContext) {
                memoraPathwayMeasureContext.font = `${weight} ${fontSize}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif`;
                const measured = memoraPathwayMeasureContext.measureText(value).width;
                if (measured > 0) return measured;
            }
        } catch (_error) {
            memoraPathwayMeasureContext = null;
        }
    }
    let width = 0;
    for (const char of value) {
        if (MEMORA_PATHWAY_NARROW_CHARS.has(char)) width += fontSize * 0.32;
        else if (MEMORA_PATHWAY_WIDE_CHARS.has(char)) width += fontSize * 0.85;
        else if (char === ' ') width += fontSize * 0.27;
        else if (char >= '0' && char <= '9') width += fontSize * 0.56;
        else if (char === char.toUpperCase() && char !== char.toLowerCase()) width += fontSize * 0.66;
        else width += fontSize * 0.545;
    }
    return width;
}

function wrapMemoraPathwayText(text, maxWidth, fontSize, weight) {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) return [''];
    const lines = [];
    let current = '';
    words.forEach((word) => {
        const candidate = current ? `${current} ${word}` : word;
        if (measureMemoraPathwayText(candidate, fontSize, weight) <= maxWidth || !current) {
            current = candidate;
            return;
        }
        lines.push(current);
        current = word;
    });
    if (current) lines.push(current);

    const { nodeMaxLines } = MEMORA_PATHWAY_METRICS;
    if (lines.length <= nodeMaxLines) return lines;
    // Fold the overflow into the last allowed line and ellipsize it. Truncating
    // is better than an unbounded box that shoves the whole rank sideways.
    const kept = lines.slice(0, nodeMaxLines - 1);
    let tail = lines.slice(nodeMaxLines - 1).join(' ');
    while (tail.length > 1 && measureMemoraPathwayText(`${tail}…`, fontSize, weight) > maxWidth) {
        tail = tail.slice(0, -1);
    }
    kept.push(`${tail.trim()}…`);
    return kept;
}

/* ==========================================================================
   PARSER
   ========================================================================== */

function normalizeMemoraPathwayRole(raw = '') {
    const clean = String(raw || '').trim().toLowerCase().replace(/[^a-z]/g, '');
    if (!clean) return '';
    if (MEMORA_PATHWAY_ROLES[clean]) return clean;
    return MEMORA_PATHWAY_ROLE_ALIASES[clean] || '';
}

function memoraPathwayNodeKey(label = '') {
    return String(label || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[.,;:]+$/, '')
        .trim();
}

/**
 * Alias keys for one node label.
 *
 * Models name a compound box in full once ("PGG2 / PGH2") and then refer to one
 * member of it later ("PGH2 -> Thromboxane A2"). Without this the short form
 * becomes a second, parentless box and the cascade renders as two disconnected
 * fragments - which is exactly what the first arachidonic-acid test produced.
 *
 * Matching is deliberately narrow: only a WHOLE separator-delimited component
 * counts, and only at 3+ characters. "PGH2" therefore resolves into
 * "PGG2 / PGH2", while "PGE2" and "PGD2" stay distinct from each other.
 */
function memoraPathwayAliasKeys(label = '') {
    const key = memoraPathwayNodeKey(label);
    if (!key) return [];
    const aliases = new Set([key]);
    key.split(/\s*(?:\/|,|;|\bor\b|\band\b|\+)\s*/)
        .map((part) => part.trim())
        .filter((part) => part.length >= 3 && part !== key)
        .forEach((part) => aliases.add(part));
    return Array.from(aliases);
}

/** "PGH2 {product}" -> { label: 'PGH2', role: 'product' } */
function splitMemoraPathwayTerm(raw = '') {
    let label = String(raw || '').trim();
    let role = '';
    const tag = label.match(/\{([^}]*)\}\s*$/);
    if (tag) {
        role = normalizeMemoraPathwayRole(tag[1]);
        label = label.slice(0, tag.index).trim();
    }
    label = label
        .replace(/^[-*+•]\s+/, '')
        .replace(/\*\*/g, '')
        .replace(/`+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    return { label, role };
}

/**
 * A label has to be real text, not a fragment.
 *
 * Without this, a stray separator produced a node whose label rendered as an
 * empty box - visible in a real guide as a blank rounded rectangle wired into
 * the cascade. Requiring two alphanumerics drops the fragment and, with it, the
 * edge that pointed at it.
 */
function isUsableMemoraPathwayLabel(label = '') {
    const value = String(label || '').trim();
    if (!value) return false;
    // Must contain readable text. Kept at one alphanumeric because real cascades
    // do use very short names - C1, C3b, Xa, IIa - and rejecting those would be
    // worse than the punctuation fragments this is here to catch.
    if (!/[a-z0-9]/i.test(value)) return false;
    /* Bare connective words are always a mis-split, never a real step. Single
       letters are NOT in this list: "a" would also reject a legitimate one-letter
       box, and an empty box is already caught downstream. */
    return !/^(?:and|or|the|an|of|to|via|by|in|on|with|then|from|for)$/i.test(value);
}

function parseMemoraPathwayBlock(source = '') {
    const rawLines = String(source || '').replace(/\r\n?/g, '\n').split('\n');
    const meta = { title: '', subtitle: '', note: '', side: 'full' };
    const nodes = new Map();
    const aliasIndex = new Map();   // alias key -> canonical node key
    const edges = [];
    const activations = [];
    const inhibitions = [];

    const resolveNode = (label) => {
        const key = memoraPathwayNodeKey(label);
        if (!key) return null;
        if (nodes.has(key)) return nodes.get(key);
        const canonical = aliasIndex.get(key);
        return canonical ? nodes.get(canonical) || null : null;
    };

    const ensureNode = (label, role = '') => {
        if (!isUsableMemoraPathwayLabel(label)) return null;
        const key = memoraPathwayNodeKey(label);
        if (!key) return null;
        const existing = resolveNode(label);
        if (existing) {
            if (role && !existing.role) existing.role = role;
            return existing;
        }
        if (nodes.size >= MEMORA_PATHWAY_MAX_NODES) return null;
        const node = { key, label: label.trim(), role: role || '' };
        nodes.set(key, node);
        // Longer labels claim their components, but never steal an alias that a
        // node already owns outright.
        memoraPathwayAliasKeys(label).forEach((alias) => {
            if (!nodes.has(alias) && !aliasIndex.has(alias)) aliasIndex.set(alias, key);
        });
        return node;
    };

    const matchesPathwayLabel = (targetKey, label) => {
        const key = memoraPathwayNodeKey(label);
        return !!(key && targetKey && (
            key === targetKey
            || key.includes(targetKey)
            || targetKey.includes(key)
        ));
    };

    const rawGroups = [];

    rawLines.forEach((rawLine) => {
        const line = String(rawLine || '').trim();
        if (!line || /^(?:#|\/\/)/.test(line)) return;

        // --- presentation groups: "group: Title | subtitle : Member; Member" ---
        // Grouping only organises the drawing; members are resolved against the
        // graph once every edge is known, and unknown names are dropped.
        const groupLine = line.match(/^group\s*:\s*(.+)$/i);
        if (groupLine) {
            const body = groupLine[1];
            const colon = body.indexOf(':');
            if (colon < 0) return;
            const [rawTitle, ...rawSubtitle] = body.slice(0, colon).split('|');
            const membersText = body.slice(colon + 1);
            const members = (membersText.includes(';') ? membersText.split(';') : membersText.split(','))
                .map((member) => splitMemoraPathwayTerm(member).label)
                .filter(Boolean);
            if (members.length) {
                rawGroups.push({
                    title: String(rawTitle || '').trim(),
                    subtitle: rawSubtitle.join('|').trim(),
                    members
                });
            }
            return;
        }

        // --- header keys ---
        const header = line.match(/^(title|subtitle|caption|note|side|placement|align)\s*:\s*(.+)$/i);
        if (header) {
            const key = header[1].toLowerCase();
            const value = header[2].trim();
            if (key === 'title') meta.title = value;
            else if (key === 'subtitle' || key === 'caption') meta.subtitle = value;
            else if (key === 'note') meta.note = value;
            else {
                const side = value.toLowerCase().replace(/[^a-z]/g, '');
                if (side === 'left' || side === 'right') meta.side = side;
                else if (side === 'full' || side === 'wide' || side === 'center' || side === 'centre') meta.side = 'full';
            }
            return;
        }

        // --- inhibition: "Drug -| target" ---
        if (/-\|/.test(line) && !/->/.test(line)) {
            const [rawFrom, rawTo] = line.split(/-\|/);
            const from = splitMemoraPathwayTerm(rawFrom);
            const to = splitMemoraPathwayTerm(rawTo);
            if (!from.label || !to.label) return;
            if (inhibitions.length >= MEMORA_PATHWAY_MAX_EDGES) return;
            const fromNode = ensureNode(from.label, from.role || 'drug');
            if (!fromNode) return;
            inhibitions.push({
                fromKey: fromNode.key,
                targetLabel: to.label,
                targetKey: memoraPathwayNodeKey(to.label)
            });
            return;
        }

        // --- conversion: "A -> B : enzyme", possibly chained "A -> B -> C" ---
        if (!/->|→/.test(line)) return;

        const splitParts = (text) => text.split(/->|→/)
            .map((part) => splitMemoraPathwayTerm(part))
            .filter((part) => isUsableMemoraPathwayLabel(part.label));

        let body = line;
        let enzyme = '';
        /* Only the LAST colon-separated tail is an enzyme label, and only when it
           sits after the final arrow: "PGH2 -> TXA2 : TX synthase" splits, while
           "Note: A -> B" keeps its arrow.

           The tail also has to LOOK like an enzyme. A model that writes
           "A -> Müllerian duct: development" means the colon as punctuation
           inside the box name, and treating "development" as the enzyme leaves a
           node fragment behind - which is how a box reading just "Development"
           ended up in a real guide. Long tails are left as part of the label. */
        const colon = body.lastIndexOf(':');
        if (colon > body.lastIndexOf('>') && colon > -1) {
            const tail = body.slice(colon + 1).trim();
            const head = body.slice(0, colon);
            const tailIsEnzymeish = tail.length > 0
                && tail.length <= 60
                && tail.split(/\s+/).length <= 6;
            // Never let the split destroy the statement: if the head no longer
            // contains a usable edge, the colon was punctuation, not a separator.
            if (tailIsEnzymeish && splitParts(head).length >= 2) {
                enzyme = tail;
                body = head;
            }
        }
        const parts = splitParts(body);
        if (parts.length < 2) return;
        for (let i = 0; i < parts.length - 1; i += 1) {
            if (edges.length >= MEMORA_PATHWAY_MAX_EDGES) break;
            const from = ensureNode(parts[i].label, parts[i].role);
            const to = ensureNode(parts[i + 1].label, parts[i + 1].role);
            if (!from || !to || from.key === to.key) continue;
            const label = splitMemoraPathwayTerm(enzyme).label;
            edges.push({
                fromKey: from.key,
                toKey: to.key,
                // A chained line's enzyme label belongs to the final hop only.
                label: i === parts.length - 2 ? label : ''
            });
        }
    });

    /* Positive regulators are authored with the ordinary arrow grammar so the
       model does not need another symbol:

         Cholesterol -> Androstenedione : cholesterol desmolase
         LH -> cholesterol desmolase : stimulates

       The second line points to the first line's enzyme label, not to a new
       biochemical intermediate. Convert that temporary edge into an external
       regulatory input before graph ranking; otherwise the renderer sees two
       disconnected linear chains and incorrectly chooses the horizontal view. */
    const activationCandidates = edges.map((edge) => {
        if (!/^(?:stimulates?|activates?|induces?|promotes?|enhances?|up[- ]?regulates?)$/i.test(edge.label || '')) return null;
        const targetEdge = edges.find((candidate) => (
            candidate !== edge && matchesPathwayLabel(edge.toKey, candidate.label)
        ));
        return targetEdge ? { edge, targetEdge } : null;
    }).filter(Boolean);
    if (activationCandidates.length) {
        const activationEdges = new Set(activationCandidates.map((entry) => entry.edge));
        activationCandidates.forEach(({ edge, targetEdge }) => {
            activations.push({
                fromKey: edge.fromKey,
                targetLabel: nodes.get(edge.toKey)?.label || edge.toKey,
                targetKey: edge.toKey,
                targetType: 'edge',
                targetEdge
            });
        });
        for (let index = edges.length - 1; index >= 0; index -= 1) {
            if (activationEdges.has(edges[index])) edges.splice(index, 1);
        }
        activations.forEach((entry) => {
            entry.edgeIndex = edges.indexOf(entry.targetEdge);
            delete entry.targetEdge;
        });
    }

    if (!edges.length) return null;

    // Inhibition targets resolve against node names first, then enzyme labels on
    // edges - "NSAIDs -| COX" is the shape every lecture uses, and COX is drawn
    // as an edge label rather than a box.
    const resolved = inhibitions.map((entry) => {
        const targetNode = nodes.get(entry.targetKey)
            || nodes.get(aliasIndex.get(entry.targetKey) || '');
        if (targetNode && targetNode.key !== entry.fromKey) {
            return { ...entry, targetKey: targetNode.key, targetType: 'node' };
        }
        const edgeIndex = edges.findIndex((edge) => {
            return matchesPathwayLabel(entry.targetKey, edge.label);
        });
        if (edgeIndex >= 0) return { ...entry, targetType: 'edge', edgeIndex };
        return null;
    }).filter(Boolean);

    // Default roles from graph position, so an untagged block still reads.
    const hasIncoming = new Set(edges.map((edge) => edge.toKey));
    const hasOutgoing = new Set(edges.map((edge) => edge.fromKey));
    nodes.forEach((node) => {
        if (node.role) return;
        if (!hasIncoming.has(node.key)) node.role = MEMORA_PATHWAY_ROLES.start;
        else if (!hasOutgoing.has(node.key)) node.role = MEMORA_PATHWAY_ROLES.product;
        else node.role = MEMORA_PATHWAY_ROLES.substrate;
    });
    const flowKeys = new Set(edges.flatMap((edge) => [edge.fromKey, edge.toKey]));
    activations.forEach((entry) => {
        const regulator = nodes.get(entry.fromKey);
        if (regulator && !flowKeys.has(regulator.key)) {
            regulator.role = regulator.role || MEMORA_PATHWAY_ROLES.start;
            regulator.isRegulator = true;
        }
        const proxy = nodes.get(entry.targetKey);
        if (proxy && !flowKeys.has(proxy.key)) proxy.isRegulationProxy = true;
    });
    // A drug that only inhibits sits outside the flow entirely.
    resolved.forEach((entry) => {
        const node = nodes.get(entry.fromKey);
        if (node && !hasIncoming.has(node.key) && !hasOutgoing.has(node.key)) {
            node.role = MEMORA_PATHWAY_ROLES.drug;
            node.isInhibitor = true;
        }
    });

    const groupedKeys = new Set();
    const groups = rawGroups.map((group) => {
        const keys = group.members
            .map((label) => resolveNode(label)?.key)
            .filter((key) => key && !groupedKeys.has(key));
        keys.forEach((key) => groupedKeys.add(key));
        return { title: group.title, subtitle: group.subtitle, keys };
    }).filter((group) => group.keys.length);

    return { meta, nodes, edges, activations, inhibitions: resolved, groups };
}

/* ==========================================================================
   LAYOUT
   Layered (Sugiyama-lite): rank by longest path, order by barycenter, then a
   median x-pass that straightens long chains.
   ========================================================================== */

function layoutMemoraPathway(parsed, options = {}) {
    const requestedMaxWidth = Number(options?.maxWidth);
    const hasWidthConstraint = Number.isFinite(requestedMaxWidth) && requestedMaxWidth > 0;
    const constrainedWidth = hasWidthConstraint ? Math.max(260, requestedMaxWidth) : 0;
    const M = hasWidthConstraint
        ? {
            ...MEMORA_PATHWAY_METRICS,
            // Preserve the 13px type while giving it narrower boxes to wrap into.
            // The graph therefore reflows vertically instead of being uniformly
            // scaled down until its labels become unreadable.
            nodeMaxWidth: Math.max(
                MEMORA_PATHWAY_METRICS.nodeMinWidth + (MEMORA_PATHWAY_METRICS.nodePadX * 2),
                Math.min(MEMORA_PATHWAY_METRICS.nodeMaxWidth, Math.round(130 + (constrainedWidth * 0.06)))
            ),
            siblingGap: Math.max(20, Math.min(
                MEMORA_PATHWAY_METRICS.siblingGap,
                Math.round(16 + (constrainedWidth * 0.015))
            ))
        }
        : MEMORA_PATHWAY_METRICS;
    const flowNodes = Array.from(parsed.nodes.values()).filter((node) => (
        !node.isInhibitor && !node.isRegulator && !node.isRegulationProxy
    ));
    const flowKeys = new Set(flowNodes.map((node) => node.key));
    const edges = parsed.edges.filter((edge) => flowKeys.has(edge.fromKey) && flowKeys.has(edge.toKey));
    if (!flowNodes.length || !edges.length) return null;

    const byKey = new Map(flowNodes.map((node) => [node.key, {
        ...node,
        parents: [],
        children: [],
        rank: 0,
        order: 0,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        lines: []
    }]));
    edges.forEach((edge) => {
        byKey.get(edge.fromKey).children.push(edge.toKey);
        byKey.get(edge.toKey).parents.push(edge.fromKey);
    });

    /* --- feedback edges ---------------------------------------------------
       Walk forward from the real starting boxes (then from declaration order, for a
       pure loop) and mark the edge that closes each cycle as feedback. Ranking
       ignores those, so "Cortisol -> Hypothalamus" becomes the loop back to the
       top instead of dragging the Hypothalamus to the bottom of the drawing. */
    const feedbackLinks = new Set();
    const walkState = new Map();
    const walk = (key) => {
        walkState.set(key, 1);
        byKey.get(key).children.forEach((childKey) => {
            const state = walkState.get(childKey);
            if (state === 1) feedbackLinks.add(`${key}\u0000${childKey}`);
            else if (!state) walk(childKey);
        });
        walkState.set(key, 2);
    };
    const walkOrder = Array.from(byKey.keys());
    walkOrder.filter((key) => !byKey.get(key).parents.length).forEach((key) => { if (!walkState.has(key)) walk(key); });
    walkOrder.forEach((key) => { if (!walkState.has(key)) walk(key); });

    // --- rank: longest path from any source, feedback edges excluded ---
    const ranked = new Set();
    const visiting = new Set();
    const assignRank = (key) => {
        const node = byKey.get(key);
        if (ranked.has(key)) return node.rank;
        if (visiting.has(key)) return node.rank; // defensive: never loop
        visiting.add(key);
        let rank = 0;
        node.parents.forEach((parentKey) => {
            if (feedbackLinks.has(`${parentKey}\u0000${key}`)) return;
            rank = Math.max(rank, assignRank(parentKey) + 1);
        });
        visiting.delete(key);
        node.rank = rank;
        ranked.add(key);
        return rank;
    };
    byKey.forEach((_node, key) => assignRank(key));

    /* --- long-edge routing ------------------------------------------------
       An edge that skips ranks (extrinsic Factor VIIa -> Factor Xa jumps three)
       drew as a straight line straight THROUGH the boxes sitting between them.
       Give every such edge an invisible waypoint on each rank it crosses. The
       waypoints take part in ordering and x-placement like any other node, so
       real boxes make room for the line instead of being overdrawn by it. */
    let dummySeq = 0;
    edges.forEach((edge) => {
        const from = byKey.get(edge.fromKey);
        const to = byKey.get(edge.toKey);
        if (!from || !to) return;
        const span = to.rank - from.rank;
        edge.via = [];
        if (span <= 1) return;
        let previousKey = edge.fromKey;
        for (let rank = from.rank + 1; rank < to.rank; rank += 1) {
            dummySeq += 1;
            const key = `__via_${dummySeq}`;
            const dummy = {
                key,
                label: '',
                role: '',
                isDummy: true,
                parents: [previousKey],
                children: [],
                rank,
                order: 0,
                x: 0,
                y: 0,
                width: MEMORA_PATHWAY_METRICS.viaWidth,
                height: 0,
                lines: []
            };
            byKey.set(key, dummy);
            byKey.get(previousKey).children.push(key);
            edge.via.push(key);
            previousKey = key;
        }
        byKey.get(previousKey).children.push(edge.toKey);
        to.parents.push(previousKey);
        // Detach the original direct link so ordering does not also try to keep
        // these two adjacent - the waypoint chain represents it now.
        from.children = from.children.filter((key, index, list) => (
            key !== edge.toKey || list.indexOf(key) !== index
        ));
        const directIndex = from.children.indexOf(edge.toKey);
        if (directIndex >= 0) from.children.splice(directIndex, 1);
        const parentIndex = to.parents.indexOf(edge.fromKey);
        if (parentIndex >= 0) to.parents.splice(parentIndex, 1);
    });

    // --- box sizing ---
    // Last line of defence against an empty box: whatever survived parsing must
    // still produce at least one visible line of text after wrapping, or it draws
    // as a blank rounded rectangle wired into the cascade.
    byKey.forEach((node, key) => {
        if (node.isDummy) return;
        if (!String(node.label || '').trim()) byKey.delete(key);
    });
    byKey.forEach((node) => {
        if (node.isDummy) return;
        const weight = node.role === MEMORA_PATHWAY_ROLES.effect ? 650 : 700;
        const natural = measureMemoraPathwayText(node.label, M.fontSize, weight);
        const target = Math.min(M.nodeMaxWidth - M.nodePadX * 2, Math.max(natural, 0));
        node.lines = wrapMemoraPathwayText(node.label, target, M.fontSize, weight);
        const widest = node.lines.reduce(
            (max, line) => Math.max(max, measureMemoraPathwayText(line, M.fontSize, weight)),
            0
        );
        node.width = Math.max(M.nodeMinWidth, Math.min(M.nodeMaxWidth, Math.ceil(widest) + M.nodePadX * 2));
        node.height = node.lines.length * M.lineHeight + M.nodePadY * 2;
    });

    // --- rank buckets ---
    const maxRank = Math.max(...Array.from(byKey.values()).map((node) => node.rank));
    const ranks = Array.from({ length: maxRank + 1 }, () => []);
    byKey.forEach((node) => ranks[node.rank].push(node));
    ranks.forEach((rank) => rank.forEach((node, index) => { node.order = index; }));

    // --- crossing reduction: barycenter sweeps ---
    const sweep = (direction) => {
        const order = direction === 'down'
            ? ranks.slice(1)
            : ranks.slice(0, -1).reverse();
        order.forEach((rank) => {
            rank.forEach((node) => {
                const neighbors = direction === 'down' ? node.parents : node.children;
                const positions = neighbors
                    .map((key) => byKey.get(key))
                    .filter(Boolean)
                    .map((neighbor) => neighbor.order);
                node._bary = positions.length
                    ? positions.reduce((sum, value) => sum + value, 0) / positions.length
                    : node.order;
            });
            rank.sort((a, b) => (a._bary - b._bary) || (a.order - b.order));
            rank.forEach((node, index) => { node.order = index; });
        });
    };
    for (let pass = 0; pass < 4; pass += 1) {
        sweep('down');
        sweep('up');
    }

    const centerOf = (node) => node.x + node.width / 2;
    const isForwardHop = (from, to) => !!from && !!to && to.rank === from.rank + 1;
    const forwardParents = (node) => node.parents
        .map((key) => byKey.get(key))
        .filter((parent) => isForwardHop(parent, node));
    const forwardChildren = (node) => node.children
        .map((key) => byKey.get(key))
        .filter((child) => isForwardHop(node, child));

    /* --- label reservations ----------------------------------------------
       An enzyme label sits beside the last vertical drop into its target, the
       way a textbook writes it next to the arrow. Its width is reserved on the
       target's side BEFORE horizontal placement, so neighbouring boxes pack
       around it. The old approach placed labels afterwards and then shoved
       colliding pills into vertical lanes, which detached them from their lines. */
    const labelHeight = Math.ceil(M.edgeLabelFontSize + 4);
    edges.forEach((edge) => {
        edge.labelWidth = edge.label
            ? Math.ceil(measureMemoraPathwayText(edge.label, M.edgeLabelFontSize, 600))
            : 0;
    });
    byKey.forEach((node) => {
        node.padL = 0;
        node.padR = 0;
        if (node.isDummy) return;
        const labelled = edges.filter((edge) => edge.toKey === node.key && edge.label && byKey.has(edge.fromKey));
        if (!labelled.length) return;
        const ports = Math.max(1, forwardParents(node).length);
        const maxPortOffset = ports > 1 ? (M.portSpread * (ports - 1)) / 2 : 0;
        const widest = Math.max(...labelled.map((edge) => edge.labelWidth));
        const reach = maxPortOffset + M.edgeLabelGap + widest - node.width / 2;
        node.padR = Math.max(0, Math.ceil(reach));
        // Two labelled arrows into one box: the left one's label goes on the left.
        if (labelled.length > 1) node.padL = node.padR;
    });

    // --- horizontal placement: pack, then median-straighten, then de-collide ---
    const packRank = (rank) => {
        let cursor = 0;
        rank.forEach((node) => {
            node.x = cursor + node.padL;
            cursor += node.padL + node.width + node.padR + M.siblingGap;
        });
        return cursor - M.siblingGap;
    };
    const rankWidths = ranks.map(packRank);
    const widestRank = Math.max(...rankWidths);
    ranks.forEach((rank, index) => {
        const shift = (widestRank - rankWidths[index]) / 2;
        rank.forEach((node) => { node.x += shift; });
    });

    const relax = (rank, neighborsOf) => {
        // Desired center = median of connected neighbors. Median beats mean here:
        // a 3-way branch keeps its trunk vertical instead of drifting toward the
        // heavier side.
        const desired = rank.map((node) => {
            const centers = neighborsOf(node)
                .map((key) => byKey.get(key))
                .filter(Boolean)
                .map(centerOf)
                .sort((a, b) => a - b);
            if (!centers.length) return centerOf(node);
            const mid = Math.floor(centers.length / 2);
            return centers.length % 2 ? centers[mid] : (centers[mid - 1] + centers[mid]) / 2;
        });
        rank.forEach((node, index) => { node.x = desired[index] - node.width / 2; });
        // Left-to-right then right-to-left clamp restores the minimum gap (label
        // reservations included) without undoing the straightening more than needed.
        for (let i = 1; i < rank.length; i += 1) {
            const min = rank[i - 1].x + rank[i - 1].width + rank[i - 1].padR + M.siblingGap + rank[i].padL;
            if (rank[i].x < min) rank[i].x = min;
        }
        for (let i = rank.length - 2; i >= 0; i -= 1) {
            const max = rank[i + 1].x - rank[i + 1].padL - M.siblingGap - rank[i].padR - rank[i].width;
            if (rank[i].x > max) rank[i].x = max;
        }
    };
    for (let pass = 0; pass < 3; pass += 1) {
        ranks.slice(1).forEach((rank) => relax(rank, (node) => node.parents));
        ranks.slice(0, -1).reverse().forEach((rank) => relax(rank, (node) => node.children));
    }

    /* --- ports ---------------------------------------------------------------
       A box with several parents takes each arrow at its own point along its
       top edge, ordered by where the parent sits. Arrows that all landed on one
       pixel were the knot at the centre of every merge. */
    const inPortX = new Map();
    const portKey = (fromKey, toKey) => `${fromKey}\u0000${toKey}`;
    byKey.forEach((node) => {
        const parents = forwardParents(node).sort((a, b) => centerOf(a) - centerOf(b));
        if (!parents.length) return;
        if (node.isDummy || parents.length === 1) {
            const parent = parents[0];
            // A near-vertical hop becomes truly vertical instead of a tiny kink.
            const straight = !node.isDummy
                && Math.abs(centerOf(parent) - centerOf(node)) < 6
                && centerOf(parent) > node.x + 12
                && centerOf(parent) < node.x + node.width - 12;
            parents.forEach((entry) => inPortX.set(portKey(entry.key, node.key), straight ? centerOf(parent) : centerOf(node)));
            return;
        }
        const spread = Math.min(M.portSpread, (node.width * 0.6) / (parents.length - 1));
        parents.forEach((parent, index) => {
            inPortX.set(portKey(parent.key, node.key), centerOf(node) + (index - (parents.length - 1) / 2) * spread);
        });
    });

    /* --- bus lanes ---------------------------------------------------------------
       Each parent drops one trunk and splits along a horizontal bus. Two buses in
       the same gap whose spans touch would read as one bar - which parent feeds
       which box becomes a guess - so they are staggered onto separate lanes. */
    const laneOf = new Map();
    const gapLaneCount = ranks.map(() => 1);
    ranks.forEach((rank, rankIndex) => {
        if (rankIndex === ranks.length - 1) return;
        const buses = rank.map((node) => {
            const xs = forwardChildren(node).map((child) => inPortX.get(portKey(node.key, child.key)) ?? centerOf(child));
            if (!xs.length) return null;
            xs.push(centerOf(node));
            return { key: node.key, min: Math.min(...xs), max: Math.max(...xs) };
        }).filter(Boolean).sort((a, b) => a.min - b.min);
        const laneEnds = [];
        buses.forEach((bus) => {
            let lane = 0;
            if (bus.max - bus.min >= 1) {
                lane = laneEnds.findIndex((end) => bus.min > end + M.laneClearance);
                if (lane < 0) lane = laneEnds.length;
                laneEnds[lane] = bus.max;
            }
            laneOf.set(bus.key, lane);
        });
        gapLaneCount[rankIndex] = Math.max(1, laneEnds.length);
    });

    // Labels that land in each gap, stacked when one box takes two of them on a side.
    const gapHasLabels = ranks.map(() => false);
    const gapLabelStack = ranks.map(() => 1);
    ranks.forEach((rank, rankIndex) => {
        if (!rankIndex) return;
        rank.forEach((node) => {
            if (node.isDummy) return;
            const count = edges.filter((edge) => edge.toKey === node.key && edge.label && byKey.has(edge.fromKey)).length;
            if (!count) return;
            gapHasLabels[rankIndex - 1] = true;
            gapLabelStack[rankIndex - 1] = Math.max(gapLabelStack[rankIndex - 1], Math.max(1, count - 1));
        });
    });

    // --- vertical placement: each gap is as tall as its lanes and labels need ---
    const bandTop = [];
    const bandBottom = [];
    let y = M.padding;
    ranks.forEach((rank, rankIndex) => {
        const tallest = rank.reduce((max, node) => Math.max(max, node.height), 0);
        rank.forEach((node) => {
            node.y = node.isDummy ? y + tallest / 2 : y + (tallest - node.height) / 2;
        });
        bandTop[rankIndex] = y;
        bandBottom[rankIndex] = y + tallest;
        const lanes = (gapLaneCount[rankIndex] - 1) * M.laneStep;
        const drop = gapHasLabels[rankIndex]
            ? gapLabelStack[rankIndex] * (labelHeight + 4) + 26
            : 26;
        const gap = Math.max(gapHasLabels[rankIndex] ? M.labelledRankGap : M.rankGap, M.busDrop + lanes + drop);
        y += tallest + gap;
    });
    const laneY = (node) => bandBottom[node.rank] + M.busDrop + (laneOf.get(node.key) || 0) * M.laneStep;
    const lowestLaneY = (rankIndex) => bandBottom[rankIndex] + M.busDrop + (gapLaneCount[rankIndex] - 1) * M.laneStep;

    const intersects = (a, b, pad = 8) => (
        a.x < b.x + b.width + pad
        && a.x + a.width + pad > b.x
        && a.y < b.y + b.height + pad
        && a.y + a.height + pad > b.y
    );

    /* --- routes --------------------------------------------------------------
       Orthogonal: trunk down, along the bus, straight down into the box. Corners
       are rounded at render time. A feedback edge (target not below its source)
       leaves under the source, runs up the right-hand margin, and comes down into
       the target, so it never crosses a box. */
    const flowRight = Math.max(...Array.from(byKey.values()).map((node) => node.x + node.width + node.padR));
    let loopIndex = 0;
    const dedupe = (points) => points.filter((point, index) => {
        const previous = points[index - 1];
        return !previous || Math.abs(previous.x - point.x) > 0.01 || Math.abs(previous.y - point.y) > 0.01;
    });
    edges.forEach((edge) => {
        const from = byKey.get(edge.fromKey);
        const to = byKey.get(edge.toKey);
        edge.route = null;
        if (!from || !to) return;
        if (to.rank <= from.rank) {
            const laneX = flowRight + 22 + loopIndex * 12;
            loopIndex += 1;
            const below = bandBottom[from.rank] + 7;
            const above = bandTop[to.rank] - 7;
            const entryX = centerOf(to) + Math.min(10, to.width * 0.25);
            edge.route = dedupe([
                { x: centerOf(from), y: from.y + from.height },
                { x: centerOf(from), y: below },
                { x: laneX, y: below },
                { x: laneX, y: above },
                { x: entryX, y: above },
                { x: entryX, y: to.y }
            ]);
            edge.dropX = entryX;
            edge.dropTop = above;
            return;
        }
        const chain = [from, ...(edge.via || []).map((key) => byKey.get(key)).filter(Boolean), to];
        const points = [];
        for (let i = 0; i < chain.length - 1; i += 1) {
            const u = chain[i];
            const v = chain[i + 1];
            const sx = centerOf(u);
            const sy = u.isDummy ? u.y : u.y + u.height;
            const ex = inPortX.get(portKey(u.key, v.key)) ?? centerOf(v);
            const ey = v.y;
            const ly = laneY(u);
            points.push({ x: sx, y: sy });
            if (Math.abs(sx - ex) >= 1.5) {
                points.push({ x: sx, y: ly }, { x: ex, y: ly });
            }
            points.push({ x: Math.abs(sx - ex) >= 1.5 ? ex : sx, y: ey });
            if (i === chain.length - 2) {
                edge.dropX = Math.abs(sx - ex) >= 1.5 ? ex : sx;
                edge.dropTop = Math.abs(sx - ex) >= 1.5 ? ly : sy;
            }
        }
        edge.route = dedupe(points);
    });

    // --- labels beside the final drop ---
    const edgeLabels = [];
    byKey.forEach((node) => {
        if (node.isDummy) return;
        const labelled = edges
            .filter((edge) => edge.toKey === node.key && edge.label && edge.route)
            .sort((a, b) => a.dropX - b.dropX);
        if (!labelled.length) return;
        const regionTop = node.rank > 0 ? lowestLaneY(node.rank - 1) + 6 : node.y - labelHeight - 10;
        const regionBottom = node.y - 5;
        let rightStack = 0;
        labelled.forEach((edge, index) => {
            const side = labelled.length > 1 && index === 0 ? 'left' : 'right';
            const stackIndex = side === 'left' ? 0 : rightStack++;
            const stackCount = Math.max(1, labelled.length - 1);
            const blockHeight = (side === 'left' ? 1 : stackCount) * (labelHeight + 4) - 4;
            const top = Math.max(regionTop, regionTop + (regionBottom - regionTop - blockHeight) / 2);
            const labelY = top + stackIndex * (labelHeight + 4);
            const label = {
                edgeIndex: edges.indexOf(edge),
                text: edge.label,
                side,
                width: edge.labelWidth,
                height: labelHeight,
                x: side === 'left' ? edge.dropX - M.edgeLabelGap - edge.labelWidth : edge.dropX + M.edgeLabelGap,
                y: labelY,
                dropX: edge.dropX
            };
            label.cx = label.x + label.width / 2;
            label.cy = label.y + label.height / 2;
            edge._label = label;
            edgeLabels.push(label);
        });
    });

    // Thin obstacles for every routed segment, so regulator boxes never sit on a line.
    const segmentBoxes = [];
    edges.forEach((edge) => {
        (edge.route || []).forEach((point, index, list) => {
            if (!index) return;
            const previous = list[index - 1];
            segmentBoxes.push({
                x: Math.min(previous.x, point.x) - 2,
                y: Math.min(previous.y, point.y) - 2,
                width: Math.abs(previous.x - point.x) + 4,
                height: Math.abs(previous.y - point.y) + 4
            });
        });
    });

    // --- regulators: park beside the labelled step they control ---
    const laidOut = Array.from(byKey.values()).filter((node) => !node.isDummy);
    const inhibitorBoxes = [];
    const activationBoxes = [];
    const buildRegulatorBox = (entry, kind) => {
        const node = parsed.nodes.get(entry.fromKey);
        if (!node) return null;
        const lines = wrapMemoraPathwayText(node.label, M.nodeMaxWidth - M.nodePadX * 2 - 20, M.fontSize, 700);
        const widestLine = lines.reduce(
            (max, line) => Math.max(max, measureMemoraPathwayText(line, M.fontSize, 700)),
            0
        );
        const box = {
            key: `${kind}::${entry.fromKey}::${entry.targetKey}`,
            label: node.label,
            lines,
            role: kind === 'inhibition' ? MEMORA_PATHWAY_ROLES.drug : MEMORA_PATHWAY_ROLES.start,
            kind,
            width: Math.max(M.nodeMinWidth, Math.ceil(widestLine) + M.nodePadX * 2),
            height: lines.length * M.lineHeight + M.nodePadY * 2,
            target: entry,
            x: 0,
            y: 0
        };

        let anchorX = 0;
        let anchorY = 0;
        if (entry.targetType === 'node') {
            const target = byKey.get(entry.targetKey);
            if (!target) return null;
            anchorX = target.x;
            anchorY = target.y + target.height / 2;
            box.anchor = { x: anchorX, y: anchorY, edge: 'node', targetKey: entry.targetKey };
        } else {
            const edge = edges[entry.edgeIndex];
            if (!edge?.route) return null;
            // Aim at the arrow itself, level with its enzyme label, from the side the
            // label is not on. The bar then reads as "blocks this step".
            if (edge._label) {
                anchorY = edge._label.cy;
                anchorX = edge._label.side === 'left' ? edge._label.x - 4 : edge.dropX - 5;
            } else {
                anchorY = (edge.dropTop + byKey.get(edge.toKey).y) / 2;
                anchorX = edge.dropX - 5;
            }
            box.anchor = { x: anchorX, y: anchorY, edge: 'edge', edgeIndex: entry.edgeIndex };
        }
        box.y = anchorY - box.height / 2;
        box.x = anchorX - M.inhibitorGap - box.width;
        return box;
    };
    parsed.inhibitions.forEach((entry) => {
        const box = buildRegulatorBox(entry, 'inhibition');
        if (box) inhibitorBoxes.push(box);
    });
    (parsed.activations || []).forEach((entry) => {
        const box = buildRegulatorBox(entry, 'activation');
        if (box) activationBoxes.push(box);
    });

    /* Regulators hang off the left of whatever they control, which means they land
       in the gutter between ranks - shared space that already contains node
       boxes, enzyme labels, connector lines, and other regulators. Slide each one
       further left until its lane is clear. Left, not down: the vertical position
       is the part that carries meaning (it says WHICH step is controlled). */
    const regulatorBoxes = [...inhibitorBoxes, ...activationBoxes]
        .sort((a, b) => a.y - b.y || a.x - b.x);
    regulatorBoxes.forEach((box, index) => {
        const obstacles = [
            ...laidOut,
            ...edgeLabels,
            ...regulatorBoxes.slice(0, index)
        ];
        for (let attempt = 0; attempt < 32; attempt += 1) {
            const hit = obstacles.find((other) => other !== box && intersects(box, other))
                || segmentBoxes.find((segment) => intersects(box, segment, 3));
            if (!hit) break;
            box.x = hit.x - box.width - 12;
        }
    });

    // --- normalize to a tight viewBox ---
    // Labels and routes are included in the bounds: a wide enzyme name on an outer
    // branch, or a feedback loop in the right margin, is often the outermost thing
    // in the drawing, and leaving it out clips it at the viewBox edge.
    const boxes = [
        ...laidOut,
        ...regulatorBoxes,
        ...Array.from(byKey.values()).filter((node) => node.isDummy),
        ...edgeLabels
    ];
    const routePoints = edges.flatMap((edge) => edge.route || []);
    const minX = Math.min(...boxes.map((node) => node.x), ...routePoints.map((point) => point.x));
    const maxX = Math.max(...boxes.map((node) => node.x + node.width), ...routePoints.map((point) => point.x));
    const minY = Math.min(...boxes.map((node) => node.y), ...routePoints.map((point) => point.y));
    const maxY = Math.max(...boxes.map((node) => node.y + node.height), ...routePoints.map((point) => point.y));
    const offsetX = M.padding - minX;
    const offsetY = M.padding - minY;
    boxes.forEach((node) => {
        node.x += offsetX;
        node.y += offsetY;
        if (typeof node.cx === 'number') node.cx += offsetX;
        if (typeof node.cy === 'number') node.cy += offsetY;
        if (typeof node.dropX === 'number') node.dropX += offsetX;
        if (node.anchor) {
            node.anchor.x += offsetX;
            node.anchor.y += offsetY;
        }
    });
    edges.forEach((edge) => {
        (edge.route || []).forEach((point) => {
            point.x += offsetX;
            point.y += offsetY;
        });
        if (typeof edge.dropX === 'number') edge.dropX += offsetX;
        if (typeof edge.dropTop === 'number') edge.dropTop += offsetY;
    });

    return {
        meta: parsed.meta,
        nodes: laidOut,
        byKey,
        edges,
        edgeLabels,
        inhibitors: inhibitorBoxes,
        activators: activationBoxes,
        width: Math.ceil(maxX - minX + M.padding * 2),
        height: Math.ceil(maxY - minY + M.padding * 2)
    };
}

/* ==========================================================================
   SVG RENDERING
   Colours are CSS custom properties with literal fallbacks, so the diagram
   themes with the guide and still renders standalone (harness, print).
   ========================================================================== */

function escapeMemoraPathwayText(value = '') {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Edge geometry from a routed polyline (see the ROUTES pass in the layout).
 *
 * Every corner is rounded with a quadratic whose control point is the corner
 * itself, so a trunk that splits left and right reads as one smooth fork and a
 * bus never meets a drop at a hard right angle. Coordinates are always written
 * as full x/y pairs (no H/V shorthands) so geometry checks can sample the path.
 * The last point is pulled back under the arrowhead so the line never pokes
 * through its tip.
 */
function memoraPathwayEdgePath(points = [], trimEnd = 0) {
    const M = MEMORA_PATHWAY_METRICS;
    const pts = points.map((point) => ({ x: point.x, y: point.y }));
    if (pts.length < 2) return { d: '', endX: 0, endY: 0, points: pts };
    const last = pts[pts.length - 1];
    const beforeLast = pts[pts.length - 2];
    const endX = last.x;
    const endY = last.y - trimEnd;
    if (trimEnd && Math.abs(beforeLast.x - last.x) < 0.01 && last.y - beforeLast.y > trimEnd) last.y -= trimEnd;
    const f = (value) => value.toFixed(1);
    let d = `M ${f(pts[0].x)} ${f(pts[0].y)}`;
    for (let i = 1; i < pts.length - 1; i += 1) {
        const a = pts[i - 1];
        const b = pts[i];
        const c = pts[i + 1];
        const lenIn = Math.hypot(b.x - a.x, b.y - a.y);
        const lenOut = Math.hypot(c.x - b.x, c.y - b.y);
        const r = Math.min(M.cornerRadius, lenIn / 2, lenOut / 2);
        if (r < 0.5 || !lenIn || !lenOut) {
            d += ` L ${f(b.x)} ${f(b.y)}`;
            continue;
        }
        const p1 = { x: b.x - ((b.x - a.x) / lenIn) * r, y: b.y - ((b.y - a.y) / lenIn) * r };
        const p2 = { x: b.x + ((c.x - b.x) / lenOut) * r, y: b.y + ((c.y - b.y) / lenOut) * r };
        d += ` L ${f(p1.x)} ${f(p1.y)} Q ${f(b.x)} ${f(b.y)} ${f(p2.x)} ${f(p2.y)}`;
    }
    d += ` L ${f(last.x)} ${f(last.y)}`;
    return { d, endX, endY, points: pts };
}

/* Stealth arrowhead: a notched triangle. (x, y) is the base centre for 'down'
   and the tip for 'right', matching the callers. */
function memoraPathwayArrowHead(x, y, direction = 'down') {
    const { arrowLength: len, arrowWidth: w } = MEMORA_PATHWAY_METRICS;
    const f = (value) => value.toFixed(1);
    if (direction === 'right') {
        return `M ${f(x)} ${f(y)} L ${f(x - len)} ${f(y - w / 2)} L ${f(x - len * 0.68)} ${f(y)} L ${f(x - len)} ${f(y + w / 2)} Z`;
    }
    return `M ${f(x)} ${f(y + len)} L ${f(x - w / 2)} ${f(y)} L ${f(x)} ${f(y + len * 0.32)} L ${f(x + w / 2)} ${f(y)} Z`;
}

function renderMemoraPathwayNodeSvg(node, uid) {
    const editorial = MEMORA_PATHWAY_PRESENTATION.select(uid) === 'editorial';
    const terminal = node._presentationTerminal === true;
    const M = MEMORA_PATHWAY_METRICS;
    const role = node.role || MEMORA_PATHWAY_ROLES.substrate;
    // Snap to the half pixel so 1px borders land on one device row at 100% zoom
    // instead of smearing across two.
    const x = Math.round(node.x) + 0.5;
    const y = Math.round(node.y) + 0.5;
    const width = Math.round(node.width);
    const height = Math.round(node.height);
    const textY = y + M.nodePadY + M.fontSize * 0.82;
    const lines = node.lines.map((line, index) => (
        `<tspan x="${(editorial ? x + M.nodePadX : x + width / 2).toFixed(1)}" y="${(textY + index * M.lineHeight).toFixed(1)}">${escapeMemoraPathwayText(line)}</tspan>`
    )).join('');
    return `<g class="memora-pathway-node" data-role="${escapeMemoraPathwayText(role)}">`
        + `<rect x="${x}" y="${y}" width="${width}" height="${height}" rx="10" ry="10" class="memora-pathway-node-box memora-presentation-box"${terminal ? ' data-terminal' : ''}`
        + (uid ? ` filter="url(#${escapeMemoraPathwayText(uid)}-lift)"` : '')
        + `/>`
        + MEMORA_PATHWAY_PRESENTATION.mark(x + 4, textY, Number(node.rank) + 1, terminal)
        + `<text class="memora-pathway-node-text memora-presentation-text"${terminal ? ' data-terminal' : ''} text-anchor="${editorial ? 'start' : 'middle'}">${lines}</text>`
        + `</g>`;
}

function renderMemoraPathwayEdgeLabelSvg(label) {
    const M = MEMORA_PATHWAY_METRICS;
    // No pill: the text sits beside its arrow on a halo of the canvas colour
    // (paint-order stroke in CSS), which reads as an annotation, not a button.
    return `<g class="memora-pathway-edge-label" data-side="${label.side === 'left' ? 'left' : 'right'}">`
        + `<text x="${label.x.toFixed(1)}" y="${(label.cy + M.edgeLabelFontSize * 0.36).toFixed(1)}" text-anchor="start" class="memora-pathway-edge-label-text">${escapeMemoraPathwayText(label.text)}</text>`
        + `</g>`;
}

function renderMemoraPathwaySvg(layout, uid) {
    const M = MEMORA_PATHWAY_METRICS;
    const edgeMarkup = [];
    const labelMarkup = [];

    layout.edges.forEach((edge) => {
        if (!edge.route || edge.route.length < 2) return;
        const path = memoraPathwayEdgePath(edge.route, M.arrowLength * 0.62);
        edge._path = path;
        edgeMarkup.push(
            `<path d="${path.d}" class="memora-pathway-edge" fill="none"/>`
            + `<path d="${memoraPathwayArrowHead(path.endX, path.endY + M.arrowLength * 0.62 - M.arrowLength - 0.5)}" class="memora-pathway-edge-arrow"/>`
        );
        // Geometry comes from the layout pass (edge._label) so the label sits
        // exactly where the reservation assumed it would.
        if (edge._label) labelMarkup.push(renderMemoraPathwayEdgeLabelSvg(edge._label));
    });

    const connectorPath = (startX, startY, endX, endY) => {
        if (Math.abs(startY - endY) < 1) return `M ${startX.toFixed(1)} ${startY.toFixed(1)} L ${endX.toFixed(1)} ${endY.toFixed(1)}`;
        const midX = (startX + endX) / 2;
        return `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
    };

    const inhibitorMarkup = layout.inhibitors.map((box) => {
        const anchor = box.anchor || { x: box.x + box.width, y: box.y + box.height / 2 };
        const startX = box.x + box.width;
        const startY = box.y + box.height / 2;
        const endX = anchor.x - 4;
        const endY = anchor.y;
        // Blunt "T" terminal - the universal inhibition glyph. An arrowhead here
        // would read as "becomes", which is the opposite of what it means.
        const barHalf = 8;
        const bar = `M ${endX} ${endY - barHalf} L ${endX} ${endY + barHalf}`;
        return `<g class="memora-pathway-inhibition">`
            + `<path d="${connectorPath(startX, startY, endX - 4, endY)}" class="memora-pathway-inhibition-line" fill="none"/>`
            + `<path d="${bar}" class="memora-pathway-inhibition-bar"/>`
            + renderMemoraPathwayNodeSvg({ ...box, role: MEMORA_PATHWAY_ROLES.drug }, uid)
            + `</g>`;
    }).join('');

    const activationMarkup = (layout.activators || []).map((box) => {
        const anchor = box.anchor || { x: box.x + box.width, y: box.y + box.height / 2 };
        const startX = box.x + box.width;
        const startY = box.y + box.height / 2;
        const endX = anchor.x - 4;
        const endY = anchor.y;
        return `<g class="memora-pathway-activation">`
            + `<path d="${connectorPath(startX, startY, endX - M.arrowLength * 0.6, endY)}" class="memora-pathway-activation-line" fill="none"/>`
            + `<path d="${memoraPathwayArrowHead(endX, endY, 'right')}" class="memora-pathway-activation-arrow"/>`
            + renderMemoraPathwayNodeSvg({ ...box, role: MEMORA_PATHWAY_ROLES.start }, uid)
            + `</g>`;
    }).join('');

    const nodeMarkup = layout.nodes.map((node) => renderMemoraPathwayNodeSvg({ ...node, _presentationTerminal: !node.children.length }, uid)).join('');
    const defs = uid
        ? `<defs><filter id="${escapeMemoraPathwayText(uid)}-lift" x="-20%" y="-30%" width="140%" height="190%" color-interpolation-filters="sRGB">`
            + `<feDropShadow dx="0" dy="3" stdDeviation="3.5" flood-color="#000" flood-opacity="0.32"/></filter></defs>`
        : '';

    return MEMORA_PATHWAY_PRESENTATION.svg(`<svg class="memora-pathway-svg" viewBox="0 0 ${layout.width} ${layout.height}" `
        + `width="${layout.width}" height="${layout.height}" `
        + `preserveAspectRatio="xMidYMin meet" role="img" xmlns="http://www.w3.org/2000/svg">`
        + defs
        + `<g class="memora-pathway-edges">${edgeMarkup.join('')}</g>`
        + `<g class="memora-pathway-inhibitions">${inhibitorMarkup}</g>`
        + `<g class="memora-pathway-activations">${activationMarkup}</g>`
        + `<g class="memora-pathway-nodes">${nodeMarkup}</g>`
        + `<g class="memora-pathway-edge-labels">${labelMarkup.join('')}</g>`
        + `</svg>`, uid);
}

/**
 * Plain-text fallback for copy, TTS, search, and export paths, which must never
 * pick up SVG geometry. Mirrors stripMemoraAnswerIconTokens in intent.
 */
function describeMemoraPathway(parsed) {
    if (!parsed) return '';
    const nameOf = (key) => parsed.nodes.get(key)?.label || key;
    const chains = parsed.edges.map((edge) => (
        `${nameOf(edge.fromKey)} -> ${nameOf(edge.toKey)}${edge.label ? ` (${edge.label})` : ''}`
    ));
    const blocks = parsed.inhibitions.map((entry) => (
        `${nameOf(entry.fromKey)} inhibits ${entry.targetLabel}`
    ));
    const activations = (parsed.activations || []).map((entry) => (
        `${nameOf(entry.fromKey)} stimulates ${entry.targetLabel}`
    ));
    return [parsed.meta.title, ...chains, ...activations, ...blocks, parsed.meta.note]
        .filter(Boolean)
        .join('. ');
}

function isMemoraPathwayBranched(parsed) {
    if (!parsed || !Array.isArray(parsed.edges) || !parsed.edges.length) return false;
    // A positive regulator aimed at an enzyme-labelled edge cannot be represented
    // truthfully as a second horizontal conversion chain; it is a real branch.
    if (Array.isArray(parsed.activations) && parsed.activations.length > 0) return true;
    if (parsed.edges.length < 3) return false;
    const incoming = new Map();
    const outgoing = new Map();
    parsed.edges.forEach((edge) => {
        outgoing.set(edge.fromKey, (outgoing.get(edge.fromKey) || 0) + 1);
        incoming.set(edge.toKey, (incoming.get(edge.toKey) || 0) + 1);
    });
    return Array.from(parsed.nodes?.keys?.() || []).some((key) => (
        (incoming.get(key) || 0) > 1 || (outgoing.get(key) || 0) > 1
    ));
}

function getMemoraLinearPathwayChains(parsed) {
    if (!parsed?.edges?.length) return [];
    const outgoing = new Map();
    const incoming = new Set();
    parsed.edges.forEach((edge) => {
        const list = outgoing.get(edge.fromKey) || [];
        list.push(edge);
        outgoing.set(edge.fromKey, list);
        incoming.add(edge.toKey);
    });
    const visited = new Set();
    const chains = [];
    const walk = (firstKey) => {
        const nodes = [firstKey];
        const edges = [];
        let key = firstKey;
        while (key) {
            const next = (outgoing.get(key) || []).find((edge) => !visited.has(edge));
            if (!next) break;
            visited.add(next);
            edges.push(next);
            nodes.push(next.toKey);
            if (nodes.slice(0, -1).includes(next.toKey)) break;
            key = next.toKey;
        }
        if (edges.length) chains.push({ nodes, edges });
    };

    Array.from(outgoing.keys()).filter((key) => !incoming.has(key)).forEach(walk);
    parsed.edges.forEach((edge) => {
        if (!visited.has(edge)) walk(edge.fromKey);
    });
    return chains;
}

const MEMORA_PATHWAY_LINEAR_MAX_ROW_STEPS = 5;

/**
 * A single chain drawn as step cards joined by labelled arrows.
 *
 * It used to borrow the prose mechanism scroller: 10.5px labels perched on tiny
 * arrows, in a row that scrolled sideways behind a hidden scrollbar, so the last
 * step was simply cut off. Now the row always fits - cards share the width and
 * wrap their text - and a chain longer than five steps (or a narrow container,
 * via a container query) stacks vertically with each label beside its arrow.
 */
function renderMemoraLinearPathwayBlock(parsed) {
    if (!parsed) return '';
    const chains = getMemoraLinearPathwayChains(parsed);
    if (!chains.length) return '';
    const nameOf = (key) => parsed.nodes.get(key)?.label || key;
    const title = parsed.meta.title || 'Linear pathway';
    const subtitle = parsed.meta.subtitle || '';
    // Only the enlarge control: linear steps are HTML that already wraps to fit, so the
    // resize, regenerate, and minimize controls of the SVG layouts do not apply here.
    const head = `<div class="memora-pathway-header">`
        + `<figcaption class="memora-pathway-head">`
        + `<span class="memora-pathway-title">${escapeMemoraPathwayText(title)}</span>`
        + (subtitle ? `<span class="memora-pathway-subtitle">${escapeMemoraPathwayText(subtitle)}</span>` : '')
        + `</figcaption>`
        + `<span class="memora-pathway-actions">`
        + `<button class="memora-pathway-expand" type="button" data-study-guide-pathway-expand `
        + `aria-label="Enlarge pathway diagram" title="Enlarge pathway diagram">`
        + `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">`
        + `<circle cx="10.5" cy="10.5" r="5.5"></circle>`
        + `<path d="M14.7 14.7 20 20M10.5 7.7v5.6M7.7 10.5h5.6"></path>`
        + `</svg></button></span>`
        + `</div>`;
    const vertical = chains.some((chain) => chain.nodes.length > MEMORA_PATHWAY_LINEAR_MAX_ROW_STEPS);
    const flows = chains.map((chain) => {
        const steps = chain.nodes.map((key, index) => {
            const edge = index > 0 ? chain.edges[index - 1] : null;
            const connector = edge
                ? `<div class="memora-linear-pathway-link${edge.label ? ' has-label' : ''}">`
                    + (edge.label ? `<span class="memora-linear-pathway-edge-label">${escapeMemoraPathwayText(edge.label)}</span>` : '')
                    + `<span class="memora-linear-pathway-arrow" aria-hidden="true"></span>`
                    + `</div>`
                : '';
            return connector
                + `<div class="memora-linear-pathway-step${index === 0 ? ' is-start' : ''}${index === chain.nodes.length - 1 ? ' is-end' : ''}">`
                + `<span class="memora-linear-pathway-index" aria-hidden="true">${index + 1}</span>`
                + `<span class="memora-linear-pathway-name">${escapeMemoraPathwayText(nameOf(key))}</span>`
                + `</div>`;
        }).join('');
        return `<div class="memora-linear-pathway-flow" data-linear-step-count="${chain.nodes.length}">${steps}</div>`;
    }).join('');
    const inhibitions = parsed.inhibitions.length
        ? `<div class="memora-linear-pathway-inhibitions">${parsed.inhibitions.map((entry) => (
            `<span><strong>${escapeMemoraPathwayText(nameOf(entry.fromKey))}</strong> inhibits ${escapeMemoraPathwayText(entry.targetLabel)}</span>`
        )).join('')}</div>`
        : '';
    const note = parsed.meta.note
        ? `<p class="memora-pathway-note">${escapeMemoraPathwayText(parsed.meta.note)}</p>`
        : '';
    return `<figure class="memora-linear-pathway${vertical ? ' is-vertical' : ''}" data-memora-linear-pathway `
        + `aria-label="${escapeMemoraPathwayText(`${title} linear pathway`)}">`
        + head + `<div class="memora-linear-pathway-body">${flows}</div>` + inhibitions + note
        + `<span class="memora-pathway-alt" hidden>${escapeMemoraPathwayText(describeMemoraPathway(parsed))}</span>`
        + `</figure>`;
}

/* ==========================================================================
   CONVERGING LAYOUT
   The most common lecture shape - several causes feeding one core event that
   then runs down a single cascade - reads best as grouped cause cards, two soft
   curves meeting at one junction, and a centred column of steps. It is drawn in
   HTML so text wraps naturally; every other shape keeps the layered SVG.
   ========================================================================== */

const MEMORA_PATHWAY_CONVERGING_MAX_SOURCES = 9;
const MEMORA_PATHWAY_CONVERGING_MAX_CHAIN = 8;

/**
 * Returns { sourceKeys, coreKey, chain: [{ key, label }] } when every start node
 * points straight at one core node (with no label on those arrows), and the core
 * then continues as an unbranched chain covering every other node. Otherwise null.
 */
function getMemoraConvergingPathwayShape(parsed) {
    if (!parsed?.edges?.length) return null;
    if ((parsed.inhibitions || []).length || (parsed.activations || []).length) return null;
    const incoming = new Map();
    const outgoing = new Map();
    parsed.edges.forEach((edge) => {
        const ins = incoming.get(edge.toKey) || [];
        ins.push(edge);
        incoming.set(edge.toKey, ins);
        const outs = outgoing.get(edge.fromKey) || [];
        outs.push(edge);
        outgoing.set(edge.fromKey, outs);
    });
    const flowKeys = new Set(parsed.edges.flatMap((edge) => [edge.fromKey, edge.toKey]));
    const sourceKeys = Array.from(flowKeys).filter((key) => !incoming.has(key));
    if (sourceKeys.length < 2 || sourceKeys.length > MEMORA_PATHWAY_CONVERGING_MAX_SOURCES) return null;
    const firstTargets = new Set();
    for (const key of sourceKeys) {
        const outs = outgoing.get(key) || [];
        if (outs.length !== 1 || outs[0].label) return null;
        firstTargets.add(outs[0].toKey);
    }
    if (firstTargets.size !== 1) return null;
    const coreKey = Array.from(firstTargets)[0];
    if ((incoming.get(coreKey) || []).length !== sourceKeys.length) return null;

    const chain = [];
    const seen = new Set([...sourceKeys, coreKey]);
    let key = coreKey;
    while ((outgoing.get(key) || []).length) {
        const outs = outgoing.get(key);
        if (outs.length !== 1) return null;
        const next = outs[0];
        if (seen.has(next.toKey) || (incoming.get(next.toKey) || []).length !== 1) return null;
        chain.push({ key: next.toKey, label: next.label || '' });
        seen.add(next.toKey);
        if (chain.length > MEMORA_PATHWAY_CONVERGING_MAX_CHAIN) return null;
        key = next.toKey;
    }
    if (seen.size !== flowKeys.size) return null;
    return { sourceKeys, coreKey, chain };
}

/* Minimize folds a diagram down to its header when it is too large for the moment. The choice is a
   per-viewer convenience, kept by pathway key in this browser only, and survives re-renders. */
const MEMORA_PATHWAY_MINIMIZED_STORAGE_KEY = 'memora_pathway_minimized_v1';
let memoraPathwayMinimizedKeys = null;

function getMemoraPathwayMinimizedKeys() {
    if (memoraPathwayMinimizedKeys) return memoraPathwayMinimizedKeys;
    memoraPathwayMinimizedKeys = new Set();
    try {
        const stored = JSON.parse(globalThis.localStorage?.getItem(MEMORA_PATHWAY_MINIMIZED_STORAGE_KEY) || '[]');
        if (Array.isArray(stored)) stored.slice(-200).forEach((key) => memoraPathwayMinimizedKeys.add(String(key)));
    } catch (_error) { /* storage unavailable: nothing is minimized */ }
    return memoraPathwayMinimizedKeys;
}

function setMemoraPathwayMinimized(figure, minimized) {
    const key = String(figure?.dataset?.pathwayKey || '').trim();
    if (!figure) return;
    figure.classList.toggle('is-minimized', minimized);
    const button = figure.querySelector('[data-memora-pathway-minimize]');
    if (button) {
        button.setAttribute('aria-expanded', minimized ? 'false' : 'true');
        const label = minimized ? 'Show pathway diagram' : 'Minimize pathway diagram';
        button.setAttribute('aria-label', label);
        button.title = label;
    }
    if (!key) return;
    const keys = getMemoraPathwayMinimizedKeys();
    if (minimized) keys.add(key);
    else keys.delete(key);
    try {
        globalThis.localStorage?.setItem(MEMORA_PATHWAY_MINIMIZED_STORAGE_KEY, JSON.stringify(Array.from(keys).slice(-200)));
    } catch (_error) { /* the toggle still applies for this view */ }
}

function isMemoraPathwayMinimized(pathwayKey = '') {
    return !!pathwayKey && getMemoraPathwayMinimizedKeys().has(String(pathwayKey));
}

if (typeof document !== 'undefined') {
    document.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-memora-pathway-minimize]') : null;
        const figure = button?.closest?.('.memora-pathway');
        if (!figure) return;
        event.preventDefault();
        event.stopPropagation();
        setMemoraPathwayMinimized(figure, !figure.classList.contains('is-minimized'));
    });
}

function getMemoraPathwayActionsHtml(minimized = false) {
    return `<span class="memora-pathway-actions">`
        + `<button class="memora-pathway-regenerate" type="button" data-study-guide-pathway-regenerate `
        + `aria-label="Regenerate and repair pathway diagram" title="Regenerate with source slides and a rendered-diagram check">`
        + `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">`
        + `<path d="M19 8a7.5 7.5 0 1 0 1 6M19 4v4h-4"></path>`
        + `</svg></button>`
        + `<button class="memora-pathway-expand" type="button" data-study-guide-pathway-expand `
        + `aria-label="Enlarge pathway diagram" title="Enlarge pathway diagram">`
        + `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">`
        + `<circle cx="10.5" cy="10.5" r="5.5"></circle>`
        + `<path d="M14.7 14.7 20 20M10.5 7.7v5.6M7.7 10.5h5.6"></path>`
        + `</svg></button>`
        + `<button class="memora-pathway-minimize" type="button" data-memora-pathway-minimize `
        + `aria-expanded="${minimized ? 'false' : 'true'}" `
        + `aria-label="${minimized ? 'Show pathway diagram' : 'Minimize pathway diagram'}" `
        + `title="${minimized ? 'Show pathway diagram' : 'Minimize pathway diagram'}">`
        + `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">`
        + `<path class="memora-pathway-minimize-bar" d="M6 12h12"></path>`
        + `<path class="memora-pathway-minimize-restore" d="M7 10l5 5 5-5"></path>`
        + `</svg></button></span>`;
}

/**
 * Soft converging curves from each column centre to one junction (viewBox 0 0 1000 100).
 * `weights` are the columns' relative widths (their fr values), so each curve starts
 * under the middle of its own column.
 */
function renderMemoraPathwayConnectorSvg(weights = [1]) {
    const columns = (Array.isArray(weights) && weights.length ? weights : [1])
        .map((weight) => Math.max(0.1, Number(weight) || 1));
    const total = columns.reduce((sum, weight) => sum + weight, 0);
    const junctionX = 500;
    const junctionY = 72;
    let offset = 0;
    const centres = columns.map((weight) => {
        const centre = ((offset + (weight / 2)) / total) * 1000;
        offset += weight;
        return centre;
    });
    const paths = centres.map((cx) => {
        if (Math.abs(cx - junctionX) < 1) return `<path d="M ${junctionX} 2 L ${junctionX} ${junctionY}"/>`;
        const bend = cx + ((junctionX - cx) * 0.4);
        return `<path d="M ${cx.toFixed(1)} 2 C ${cx.toFixed(1)} 34, ${bend.toFixed(1)} 50, ${junctionX} ${junctionY}"/>`;
    }).join('');
    return `<svg viewBox="0 0 1000 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">`
        + paths
        + `<path d="M ${junctionX} ${junctionY} L ${junctionX} 100"/>`
        + `</svg>`;
}

function renderMemoraGroupedPathwayBlock(parsed, shape, normalizedSource, options = {}) {
    const pathwayKey = getMemoraPathwayKey(normalizedSource);
    if (!pathwayKey) return '';
    const nameOf = (key) => parsed.nodes.get(key)?.label || key;
    const sourceSet = new Set(shape.sourceKeys);
    const groups = (parsed.groups || [])
        .map((group) => ({ ...group, keys: group.keys.filter((key) => sourceSet.has(key)) }))
        .filter((group) => group.keys.length);
    const grouped = new Set(groups.flatMap((group) => group.keys));
    const loose = shape.sourceKeys.filter((key) => !grouped.has(key));
    const hasGroups = groups.length > 0;
    if (hasGroups && loose.length) groups.push({ title: '', subtitle: '', keys: loose });

    const node = (key) => `<div class="memora-pathway-card">${escapeMemoraPathwayText(nameOf(key))}</div>`;
    // Up to three cards share one row, each column as wide as its label needs (a long cause gets
    // a wider card than a short one); larger groups fall back to three even columns.
    const groupColumns = (keys) => (keys.length <= 3
        ? keys.map((key) => `minmax(min-content, ${Math.max(1, Math.min(2.2, nameOf(key).length / 16)).toFixed(2)}fr)`).join(' ')
        : 'repeat(3, minmax(0, 1fr))');
    // Column weights: a group is as wide as the cards it holds, so a three-card group never has to
    // squeeze into the same width as a two-card one. The connector reads the same weights.
    let weights = [1];
    let causes = '';
    if (hasGroups && groups.length <= 3) {
        weights = groups.map((group) => Math.min(group.keys.length, 3));
        causes = `<div class="memora-pathway-groups" style="grid-template-columns:${weights.map((weight) => `minmax(0, ${weight}fr)`).join(' ')}">`
            + groups.map((group) => `<section class="memora-pathway-group">`
                + (group.title || group.subtitle
                    ? `<div class="memora-pathway-group-head">`
                        + (group.title ? `<strong>${escapeMemoraPathwayText(group.title)}</strong>` : '')
                        + (group.subtitle ? `<span>${escapeMemoraPathwayText(group.subtitle)}</span>` : '')
                        + `</div>`
                    : '')
                + `<div class="memora-pathway-group-cards" style="grid-template-columns:${groupColumns(group.keys)}">${group.keys.map((key) => node(key)).join('')}</div>`
                + `</section>`).join('')
            + `</div>`;
    } else {
        const columns = Math.min(shape.sourceKeys.length, 4);
        weights = Array.from({ length: columns }, () => 1);
        causes = `<div class="memora-pathway-groups is-bare" style="--memora-pathway-columns:${columns}">`
            + shape.sourceKeys.map((key) => node(key)).join('')
            + `</div>`;
    }

    const steps = [`<div class="memora-pathway-card is-core">${escapeMemoraPathwayText(nameOf(shape.coreKey))}</div>`];
    shape.chain.forEach((step) => {
        steps.push(`<div class="memora-pathway-link">`
            + `<span class="memora-pathway-link-arrow" aria-hidden="true"></span>`
            + (step.label ? `<span class="memora-pathway-link-label">${escapeMemoraPathwayText(step.label)}</span>` : '')
            + `</div>`);
        steps.push(`<div class="memora-pathway-card is-step">${escapeMemoraPathwayText(nameOf(step.key))}</div>`);
    });

    const body = causes
        + `<div class="memora-pathway-merge">${renderMemoraPathwayConnectorSvg(weights)}</div>`
        + `<div class="memora-pathway-cascade">${steps.join('')}</div>`;
    return renderMemoraGroupedPathwayFigure(parsed, pathwayKey, normalizedSource, options, body);
}

/** Shared figure shell (header, actions, note, text alternative, resize handle) for HTML templates. */
function renderMemoraGroupedPathwayFigure(parsed, pathwayKey, normalizedSource, options = {}, bodyHtml = '', extraClass = '') {
    const title = parsed.meta.title || '';
    const subtitle = parsed.meta.subtitle || '';
    const note = parsed.meta.note || '';
    const side = options.forceSide || parsed.meta.side || 'full';
    const minimized = isMemoraPathwayMinimized(pathwayKey);
    return `<figure class="memora-pathway memora-pathway-grouped${extraClass ? ` ${extraClass}` : ''}${minimized ? ' is-minimized' : ''}" id="${pathwayKey}" data-side="${escapeMemoraPathwayText(side)}" `
        + `data-pathway-key="${escapeMemoraPathwayText(pathwayKey)}" `
        + `data-pathway-source="${escapeMemoraPathwayText(encodeMemoraPathwaySource(normalizedSource))}" `
        + (Number.isFinite(Number(options.maxWidth)) && Number(options.maxWidth) > 0
            ? `data-pathway-max-width="${Math.round(Number(options.maxWidth))}" `
            : '')
        + `aria-label="${escapeMemoraPathwayText(title ? `${title} pathway diagram` : 'Pathway diagram')}">`
        + `<div class="memora-pathway-header">`
        + `<figcaption class="memora-pathway-head">`
        + `<span class="memora-pathway-title">${escapeMemoraPathwayText(title || 'Pathway')}</span>`
        + (subtitle ? `<span class="memora-pathway-subtitle">${escapeMemoraPathwayText(subtitle)}</span>` : '')
        + `</figcaption>`
        + getMemoraPathwayActionsHtml(minimized)
        + `</div>`
        + `<div class="memora-pathway-body">`
        + bodyHtml
        + (note ? `<p class="memora-pathway-note">${escapeMemoraPathwayText(note)}</p>` : '')
        + `</div>`
        + `<span class="memora-pathway-alt" hidden>${escapeMemoraPathwayText(describeMemoraPathway(parsed))}</span>`
        + `<button class="pdf-study-guide-image-resize-handle" type="button" data-study-guide-pathway-resize-handle `
        + `aria-label="Resize pathway diagram" title="Drag to resize diagram; use Left and Right arrow keys for 5% steps"></button>`
        + `</figure>`;
}

/* ==========================================================================
   DIVERGING LAYOUT
   The mirror of the converging layout: two or three starting points (tests,
   exposures, cell types) each fanning out to their own results, optionally
   meeting at one shared outcome. As a node-link graph this drew every arrow
   from one pixel and floated its labels in the gutter; as cards it reads as
   "what each one shows". Each arrow's label becomes the caption of the card it
   points at, and the shared outcome gets its own emphasised row.
   ========================================================================== */

const MEMORA_PATHWAY_DIVERGING_MAX_ROOTS = 3;
const MEMORA_PATHWAY_DIVERGING_MAX_CHILDREN = 12;

/**
 * Returns { groups: [{ key, children: [{ key, label }] }], outcomeKey } when the
 * graph is exactly: 2-3 starting boxes, each with at least one box that only it
 * points at, plus at most one unlabelled shared box that every start points at.
 * Nothing continues past the first step. Otherwise null.
 */
function getMemoraDivergingPathwayShape(parsed) {
    if (!parsed?.edges?.length) return null;
    if ((parsed.inhibitions || []).length || (parsed.activations || []).length) return null;
    const incoming = new Map();
    const outgoing = new Set();
    parsed.edges.forEach((edge) => {
        const ins = incoming.get(edge.toKey) || [];
        ins.push(edge);
        incoming.set(edge.toKey, ins);
        outgoing.add(edge.fromKey);
    });
    const flowKeys = Array.from(new Set(parsed.edges.flatMap((edge) => [edge.fromKey, edge.toKey])));
    const roots = flowKeys.filter((key) => !incoming.has(key));
    if (roots.length < 2 || roots.length > MEMORA_PATHWAY_DIVERGING_MAX_ROOTS) return null;
    const rootSet = new Set(roots);
    const groups = roots.map((key) => ({ key, children: [] }));
    const groupOf = new Map(groups.map((group) => [group.key, group]));
    let outcomeKey = '';
    let childCount = 0;
    for (const key of flowKeys) {
        if (rootSet.has(key)) continue;
        if (outgoing.has(key)) return null;
        const ins = incoming.get(key) || [];
        if (!ins.length || !ins.every((edge) => rootSet.has(edge.fromKey))) return null;
        if (ins.length === 1) {
            groupOf.get(ins[0].fromKey).children.push({ key, label: ins[0].label || '' });
            childCount += 1;
            continue;
        }
        const parents = new Set(ins.map((edge) => edge.fromKey));
        if (outcomeKey || parents.size !== roots.length || ins.length !== roots.length) return null;
        if (ins.some((edge) => edge.label)) return null;
        outcomeKey = key;
    }
    if (groups.some((group) => !group.children.length)) return null;
    if (childCount > MEMORA_PATHWAY_DIVERGING_MAX_CHILDREN) return null;
    return { groups, outcomeKey };
}

function formatMemoraPathwayCaption(label = '') {
    const text = String(label || '').trim();
    // Sentence-case a lowercase phrase; leave "5-lipoxygenase" or "ACE" alone.
    return /^[a-z][a-z]/.test(text) ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

function renderMemoraDivergingPathwayBlock(parsed, shape, normalizedSource, options = {}) {
    const pathwayKey = getMemoraPathwayKey(normalizedSource);
    if (!pathwayKey) return '';
    const nameOf = (key) => parsed.nodes.get(key)?.label || key;
    const perRow = shape.groups.length >= 3 ? 2 : 3;
    const weights = shape.groups.map((group) => Math.min(group.children.length, perRow));
    const card = (child) => `<div class="memora-pathway-card is-result">`
        + (child.label
            ? `<span class="memora-pathway-card-caption">${escapeMemoraPathwayText(formatMemoraPathwayCaption(child.label))}</span>`
            : '')
        + `<span class="memora-pathway-card-text">${escapeMemoraPathwayText(nameOf(child.key))}</span>`
        + `</div>`;
    const groups = `<div class="memora-pathway-groups" style="grid-template-columns:${weights.map((weight) => `minmax(0, ${weight}fr)`).join(' ')}">`
        + shape.groups.map((group) => `<section class="memora-pathway-group">`
            + `<div class="memora-pathway-group-head"><strong>${escapeMemoraPathwayText(nameOf(group.key))}</strong></div>`
            + `<div class="memora-pathway-group-cards" style="grid-template-columns:repeat(${Math.min(group.children.length, perRow)}, minmax(0, 1fr))">`
            + group.children.map(card).join('')
            + `</div></section>`).join('')
        + `</div>`;
    const outcome = shape.outcomeKey
        ? `<div class="memora-pathway-merge">${renderMemoraPathwayConnectorSvg(weights)}</div>`
            + `<div class="memora-pathway-cascade"><div class="memora-pathway-card is-core is-outcome">${escapeMemoraPathwayText(nameOf(shape.outcomeKey))}</div></div>`
        : '';
    return renderMemoraGroupedPathwayFigure(parsed, pathwayKey, normalizedSource, options, groups + outcome, 'memora-pathway-diverging');
}

/**
 * Main entry point. Returns figure HTML, or '' when the block is unusable so the
 * caller can fall back to rendering it as an ordinary code block.
 */
function renderMemoraPathwayBlock(source = '', options = {}) {
    const html = renderMemoraPathwayBlockContent(source, options);
    const style = MEMORA_PATHWAY_PRESENTATION.select(getMemoraPathwayKey(source));
    return html.replace(/(<figure class="[^"]*")/, `$1 data-pathway-style="${style}"`);
}

function renderMemoraPathwayBlockContent(source = '', options = {}) {
    let parsed = null;
    let layout = null;
    const normalizedSource = normalizeMemoraPathwaySourceForKey(source);
    try {
        parsed = parseMemoraPathwayBlock(normalizedSource);
        if (!parsed) return '';
        if (!isMemoraPathwayBranched(parsed)) return renderMemoraLinearPathwayBlock(parsed);
        const converging = getMemoraConvergingPathwayShape(parsed);
        if (converging) return renderMemoraGroupedPathwayBlock(parsed, converging, normalizedSource, options);
        const diverging = getMemoraDivergingPathwayShape(parsed);
        if (diverging) return renderMemoraDivergingPathwayBlock(parsed, diverging, normalizedSource, options);
        layout = layoutMemoraPathway(parsed, { maxWidth: options.maxWidth });
    } catch (error) {
        if (typeof console !== 'undefined') console.warn('Pathway render failed:', error);
        return '';
    }
    if (!layout) return '';

    const pathwayKey = getMemoraPathwayKey(normalizedSource);
    if (!pathwayKey) return '';
    const uid = pathwayKey;
    const side = options.forceSide || layout.meta.side || 'full';
    const title = layout.meta.title || '';
    const subtitle = layout.meta.subtitle || '';
    const note = layout.meta.note || '';
    const hasInhibitors = layout.inhibitors.length > 0;
    const hasActivators = (layout.activators || []).length > 0;

    const head = (title || subtitle)
        ? `<figcaption class="memora-pathway-head">`
            + (title ? `<span class="memora-pathway-title">${escapeMemoraPathwayText(title)}</span>` : '')
            + (subtitle ? `<span class="memora-pathway-subtitle">${escapeMemoraPathwayText(subtitle)}</span>` : '')
            + `</figcaption>`
        : '';
    const legend = hasInhibitors || hasActivators
        ? `<div class="memora-pathway-legend" aria-hidden="true">`
            + `<span class="memora-pathway-legend-item"><span class="memora-pathway-legend-swatch is-flow"></span>converts to</span>`
            + (hasActivators ? `<span class="memora-pathway-legend-item"><span class="memora-pathway-legend-swatch is-activate"></span>stimulates</span>` : '')
            + (hasInhibitors ? `<span class="memora-pathway-legend-item"><span class="memora-pathway-legend-swatch is-block"></span>inhibits</span>` : '')
            + `</div>`
        : '';
    const footer = note
        ? `<p class="memora-pathway-note">${escapeMemoraPathwayText(note)}</p>`
        : '';

    const minimized = isMemoraPathwayMinimized(pathwayKey);
    return `<figure class="memora-pathway${minimized ? ' is-minimized' : ''}" id="${uid}" data-side="${escapeMemoraPathwayText(side)}" `
        + `data-pathway-key="${escapeMemoraPathwayText(pathwayKey)}" `
        + `data-pathway-source="${escapeMemoraPathwayText(encodeMemoraPathwaySource(normalizedSource))}" `
        + `data-pathway-width="${layout.width}" `
        + (Number.isFinite(Number(options.maxWidth)) && Number(options.maxWidth) > 0
            ? `data-pathway-max-width="${Math.round(Number(options.maxWidth))}" `
            : '')
        + `aria-label="${escapeMemoraPathwayText(title ? `${title} pathway diagram` : 'Pathway diagram')}">`
        + getMemoraPathwayActionsHtml(minimized)
        + head
        + `<div class="memora-pathway-canvas">${renderMemoraPathwaySvg(layout, uid)}</div>`
        + legend
        + footer
        + `<span class="memora-pathway-alt" hidden>${escapeMemoraPathwayText(describeMemoraPathway(parsed))}</span>`
        + `<button class="pdf-study-guide-image-resize-handle" type="button" data-study-guide-pathway-resize-handle `
        + `aria-label="Resize pathway diagram" title="Drag to resize diagram; use Left and Right arrow keys for 5% steps"></button>`
        + `</figure>`;
}

function hasMemoraPathwayBlock(text = '') {
    return /^```memora-pathway\b/im.test(String(text || ''));
}

/** Replace pathway blocks with their plain-text description (copy/TTS/export). */
function stripMemoraPathwayBlocks(text = '') {
    return String(text || '').replace(
        /^```memora-pathway[^\n]*\n([\s\S]*?)^```[ \t]*$/gim,
        (_match, body) => {
            try {
                const parsed = parseMemoraPathwayBlock(body);
                return parsed ? describeMemoraPathway(parsed) : '';
            } catch (_error) {
                return '';
            }
        }
    );
}

/* ==========================================================================
   PROMPT CONTRACT
   Kept next to the parser on purpose: the grammar and the thing that teaches
   the grammar drift apart the moment they live in different files.
   ========================================================================== */

function getMemoraPathwayPromptContract() {
    return `Memora pathway diagram contract:
- Reserve a \`\`\`memora-pathway block for a real multi-step biological pathway with named intermediates AND at least one meaningful branch, convergence, or external regulator aimed at a specific conversion step. Good fits include branched biochemical, signalling, metabolic, coagulation/complement, and regulatory cascades. Memora draws the diagram; you only describe it.
- Put the block immediately after the heading or paragraph that introduces the pathway, on its own lines. Also keep the prose explanation; the diagram supplements it, it does not replace it.
- Never use the SVG pathway block for a single linear sequence. Write a linear sequence on one Markdown line with named steps separated by ->; Memora renders it as a compact left-to-right container with horizontal scrolling.
- A two-item comparison is a table, not a pathway. A vertical list of treatment steps is prose or a numbered list, not a pathway.
- Never draw more than about 14 boxes in one diagram. Split a long cascade at a natural branch point instead of drawing all of it.
- Never emit SVG, coordinates, Mermaid, or ASCII art. Only the grammar below.

Grammar (one statement per line, inside the fence):
  title: <diagram title>            optional
  subtitle: <slide citation>        optional, e.g. "Slides 12-14"
  side: left | right | full         optional, default full; use left/right only for a narrow diagram of 6 boxes or fewer
  note: <one short caption line>    optional
  A -> B : <enzyme or trigger>      a conversion step; the ": ..." part is optional
  A -> B -> C                       chained steps are allowed on one line
  LH -> enzyme label : stimulates  positive regulation; exactly reuse an enzyme label from a conversion line
  Drug -| COX-1                     inhibition; the target may be a box name OR an enzyme label used on a "->" line
  Name {role}                       optional role tag: start, substrate, product, effect, drug, enzyme
  group: <title> | <subtitle> : A; B   optional; labels starting boxes that share a kind, e.g. extrinsic vs intrinsic causes

Example:
\`\`\`memora-pathway
title: Arachidonic Acid Cascade
subtitle: Slides 12-14
Membrane phospholipids -> Arachidonic acid : phospholipase A2
Arachidonic acid -> PGG2 / PGH2 : cyclooxygenase (COX-1, COX-2)
Arachidonic acid -> 5-HPETE : 5-lipoxygenase
PGH2 -> Thromboxane A2 : thromboxane synthase
PGH2 -> Prostacyclin (PGI2) : prostacyclin synthase
5-HPETE -> Leukotrienes C4, D4, E4 {product}
Corticosteroids -| phospholipase A2
NSAIDs -| cyclooxygenase (COX-1, COX-2)
note: Steroids act upstream of NSAIDs, so they block both arms.
\`\`\`

Example with groups (several causes converging on one event, then a cascade):
\`\`\`memora-pathway
title: Hemolytic Anemia Pathway
subtitle: Slide 5
group: Extrinsic causes | External or acquired drivers : Immune attack; Infection / splenomegaly / drugs
group: Intrinsic defects | Inherited RBC abnormalities : Spherocytosis; Enzymopathy; Hemoglobinopathy
Immune attack -> RBC injury or destruction
Infection / splenomegaly / drugs -> RBC injury or destruction
Spherocytosis -> RBC injury or destruction
Enzymopathy -> RBC injury or destruction
Hemoglobinopathy -> RBC injury or destruction
RBC injury or destruction -> Marrow response -> Reticulocytosis -> Indirect bilirubin / jaundice
\`\`\`

Rules:
- Every name must come from the lecture. Do not add a step, an enzyme, or a drug the source does not teach.
- Keep box labels under about 5 words. Put the detail in the surrounding prose, not in the box.
- A box label must be a complete, self-contained noun phrase: "Wolffian duct development", not "Development". A reader must understand the box without reading the one above it.
- Never put a colon inside a box label. The only colon on a step line is the one that introduces the enzyme. Write "A -> Wolffian duct development", never "A -> Wolffian duct: development".
- The text after ":" must be a short enzyme, catalyst, or trigger name - at most a few words. If it is a phrase or a sentence, it belongs in the prose instead.
- Write enzymes as the ": label" on a conversion arrow, not as their own box. The one exception is a positive-regulation line such as "LH -> cholesterol desmolase : stimulates"; its destination must repeat the conversion arrow's enzyme label exactly so Memora attaches the regulator to that step.
- Arrow direction always means precursor/substrate/cause -> product/effect. The enzyme or trigger after ":" acts on that arrow; never reverse the arrow into enzyme -> substrate. For example, write "Trypsinogen -> Trypsin : enteropeptidase", not "Trypsin -> Trypsinogen : enteropeptidase".
- Use group: lines only for starting boxes that feed the same step, two or three groups at most, each with a short title and an optional short subtitle. Separate members with ";" and spell each exactly as it appears on a "->" line.
- Do not put markdown, icon tokens, citations, or bold markers inside the block.
- When a diagram is present, do NOT also describe the same cascade as a long arrow chain in the prose beside it. State what it means; the diagram carries the sequence.`;
}

if (typeof window !== 'undefined') {
    window.MemoraPathways = {
        render: renderMemoraPathwayBlock,
        parse: parseMemoraPathwayBlock,
        layout: layoutMemoraPathway,
        describe: describeMemoraPathway,
        isBranched: isMemoraPathwayBranched,
        strip: stripMemoraPathwayBlocks,
        has: hasMemoraPathwayBlock,
        promptContract: getMemoraPathwayPromptContract
    };
}

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        renderMemoraPathwayBlock,
        parseMemoraPathwayBlock,
        layoutMemoraPathway,
        describeMemoraPathway,
        isMemoraPathwayBranched,
        renderMemoraLinearPathwayBlock,
        stripMemoraPathwayBlocks,
        hasMemoraPathwayBlock,
        getMemoraPathwayPromptContract,
        getMemoraPathwayKey,
        MEMORA_PATHWAY_METRICS,
        presentation: MEMORA_PATHWAY_PRESENTATION
    };
}
